import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiService, WaiverResponse, AdminStats } from '../services/api';
import { 
  Search, 
  LogOut, 
  FileText, 
  Calendar,
  AlertTriangle,
  CheckCircle,
  ExternalLink,
  Eye,
  Filter
} from 'lucide-react';

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout, isAuthenticated } = useAuth();
  
  const [waivers, setWaivers] = useState<WaiverResponse[]>([]);
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedWaiver, setSelectedWaiver] = useState<WaiverResponse | null>(null);

  const fetchStats = async () => {
    try {
      const response = await apiService.getStats();
      setStats(response.data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  const fetchWaivers = React.useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiService.getWaivers({
        search: searchTerm,
        status: statusFilter,
        page: currentPage,
        limit: 20,
      });
      
      setWaivers(response.data.waivers);
      setTotalPages(response.data.pagination.pages);
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch waivers');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, statusFilter, currentPage]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin/login');
      return;
    }
    
    fetchStats();
    fetchWaivers();
  }, [isAuthenticated, navigate, fetchWaivers]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchWaivers();
  };

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (isValid: boolean, expiresAt: string) => {
    const isExpiring = new Date(expiresAt) < new Date(Date.now() + 30 * 24 * 60 * 60 * 1000); // 30 days
    
    if (!isValid) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <AlertTriangle className="w-3 h-3 mr-1" />
          Expired
        </span>
      );
    }
    
    if (isExpiring) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          <AlertTriangle className="w-3 h-3 mr-1" />
          Expiring Soon
        </span>
      );
    }
    
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
        <CheckCircle className="w-3 h-3 mr-1" />
        Valid
      </span>
    );
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
              <p className="text-gray-600">Manage customer waivers</p>
            </div>
            
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600">
                Welcome, {user?.email}
              </span>
              <button
                onClick={handleLogout}
                className="flex items-center px-4 py-2 text-sm text-red-600 border border-red-300 rounded-lg hover:bg-red-50"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <FileText className="w-8 h-8 text-blue-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Waivers</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.total_waivers}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <CheckCircle className="w-8 h-8 text-green-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Valid Waivers</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.valid_waivers}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <AlertTriangle className="w-8 h-8 text-orange-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Expired</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.expired_waivers}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <Calendar className="w-8 h-8 text-purple-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">This Week</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.this_week}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="p-6">
            <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                  <input
                    type="text"
                    placeholder="Search by name or email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
              
              <div className="sm:w-40">
                <div className="relative">
                  <Filter className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="w-full pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none"
                  >
                    <option value="all">All Status</option>
                    <option value="valid">Valid Only</option>
                    <option value="expired">Expired Only</option>
                  </select>
                </div>
              </div>
              
              <button
                type="submit"
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Search
              </button>
            </form>
          </div>
        </div>

        {/* Waivers Table */}
        <div className="bg-white rounded-lg shadow">
          {error && (
            <div className="p-4 bg-red-50 border-l-4 border-red-400">
              <p className="text-red-700">{error}</p>
            </div>
          )}

          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading waivers...</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Customer
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Details
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Dates
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {waivers.map((waiver) => (
                      <tr key={waiver.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {waiver.user.name}
                            </div>
                            <div className="text-sm text-gray-500">
                              {waiver.user.email}
                            </div>
                            {waiver.user.phone && (
                              <div className="text-sm text-gray-500">
                                {waiver.user.phone}
                              </div>
                            )}
                          </div>
                        </td>
                        
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {waiver.signedFor === 'self' ? 'Individual' : 
                             waiver.signedFor === 'family' ? 'Family' : 'Group'}
                          </div>
                          {waiver.numKids > 0 && (
                            <div className="text-sm text-gray-500">
                              {waiver.numKids} kid{waiver.numKids !== 1 ? 's' : ''} 
                              {waiver.kidsAges && waiver.kidsAges.length > 0 && 
                                ` (ages: ${waiver.kidsAges.join(', ')})`}
                            </div>
                          )}
                        </td>
                        
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(waiver.isValid, waiver.expiresAt)}
                        </td>
                        
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <div>Signed: {formatDate(waiver.signedAt)}</div>
                          <div>Expires: {new Date(waiver.expiresAt).toLocaleDateString()}</div>
                        </td>
                        
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => setSelectedWaiver(waiver)}
                              className="text-blue-600 hover:text-blue-900 flex items-center"
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              View
                            </button>
                            
                            {waiver.signatureUrl && (
                              <a
                                href={waiver.signatureUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-green-600 hover:text-green-900 flex items-center"
                              >
                                <ExternalLink className="w-4 h-4 mr-1" />
                                Signature
                              </a>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="px-6 py-3 border-t border-gray-200">
                  <div className="flex justify-between items-center">
                    <div className="text-sm text-gray-700">
                      Page {currentPage} of {totalPages}
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                        className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        disabled={currentPage === totalPages}
                        className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {waivers.length === 0 && !loading && (
                <div className="p-8 text-center text-gray-500">
                  <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No waivers found matching your criteria.</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Waiver Detail Modal */}
      {selectedWaiver && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-96 overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Waiver Details</h3>
                <button
                  onClick={() => setSelectedWaiver(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-gray-900">Customer Information</h4>
                  <div className="mt-2 text-sm text-gray-600">
                    <p><strong>Name:</strong> {selectedWaiver.user.name}</p>
                    <p><strong>Email:</strong> {selectedWaiver.user.email}</p>
                    {selectedWaiver.user.phone && (
                      <p><strong>Phone:</strong> {selectedWaiver.user.phone}</p>
                    )}
                  </div>
                </div>
                
                <div>
                  <h4 className="font-medium text-gray-900">Waiver Information</h4>
                  <div className="mt-2 text-sm text-gray-600">
                    <p><strong>Type:</strong> {selectedWaiver.signedFor}</p>
                    <p><strong>Number of Kids:</strong> {selectedWaiver.numKids}</p>
                    {selectedWaiver.kidsAges && selectedWaiver.kidsAges.length > 0 && (
                      <p><strong>Kids Ages:</strong> {selectedWaiver.kidsAges.join(', ')}</p>
                    )}
                    <p><strong>Signed:</strong> {formatDate(selectedWaiver.signedAt)}</p>
                    <p><strong>Expires:</strong> {new Date(selectedWaiver.expiresAt).toLocaleDateString()}</p>
                    <p><strong>Status:</strong> {selectedWaiver.isValid ? 'Valid' : 'Expired'}</p>
                  </div>
                </div>

                {selectedWaiver.signatureUrl && (
                  <div>
                    <h4 className="font-medium text-gray-900">Digital Signature</h4>
                    <div className="mt-2">
                      <img
                        src={selectedWaiver.signatureUrl}
                        alt="Customer signature"
                        className="border border-gray-300 rounded max-w-full h-auto"
                      />
                    </div>
                  </div>
                )}
                
                {!selectedWaiver.isValid && (
                  <div className="bg-orange-50 p-4 rounded-lg">
                    <p className="text-orange-800">
                      ⚠️ This waiver has expired. Customer needs to sign a new waiver.
                    </p>
                    <button
                      onClick={() => {
                        window.open(`/waiver?email=${selectedWaiver.user.email}`, '_blank');
                      }}
                      className="mt-2 text-orange-600 hover:text-orange-800 underline"
                    >
                      Send customer to re-sign waiver
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;